import { Message as MessageDTO, useChat } from "@ai-sdk/react";
import {
  Box,
  ButtonGroup,
  Code,
  Flex,
  HStack,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  Textarea,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  RiAddLine,
  RiChatAiLine,
  RiCloseLine,
  RiFilter3Line,
  RiSendPlaneFill,
  RiSparkling2Line,
} from "@remixicon/react";
import { useQueryClient } from "@tanstack/react-query";
import { generateId } from "ai";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocalStorage } from "usehooks-ts";

import { MarkdownDisplay } from "@/common/components/MarkdownDisplay";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useResizable } from "@/common/hooks/useResizable";
import { API_URL, ENVIRONMENT } from "@/common/utils/env";
import { featureQueryKeys } from "@/feature/data/featureQueryKeys";
import { useFlag } from "@reflag/react-sdk";
import { filterMap } from "@reflag/shared/utils/array";

interface ChatState {
  id: string;
  isOpen: boolean;
  messages: MessageDTO[];
}

const isCursorAtEdge = (
  textarea: HTMLTextAreaElement,
  edge: "first" | "last",
) => {
  const { selectionStart, value } = textarea;
  return edge === "first"
    ? value.slice(0, selectionStart).indexOf("\n") === -1
    : value.slice(selectionStart).indexOf("\n") === -1;
};

const Message = ({ message }: { message: MessageDTO }) => {
  const bgColor = useColorModeValue("gray.100", "gray.700");
  const codeBgColor = useColorModeValue("gray.600", "gray.800");
  const codeTextColor = useColorModeValue("brand.200", "brand.300");

  const parts = filterMap(message.parts ?? [], (part, i) => {
    switch (part.type) {
      case "text":
        return (
          <MarkdownDisplay
            key={`${message.id}-${i}`}
            size="xs"
            value={part.text}
          />
        );
      case "tool-invocation":
        return ENVIRONMENT === "local" ? (
          <Code
            key={`${message.id}-${i}`}
            bg={codeBgColor}
            color={codeTextColor}
            whiteSpace="pre-wrap"
          >
            <Text>Executing tool {part.toolInvocation.toolName}...</Text>
            <Text>{JSON.stringify(part.toolInvocation.args, null, 2)}</Text>
          </Code>
        ) : null;
      default:
        return null;
    }
  });

  return (
    <Box
      alignSelf={message.role === "user" ? "flex-end" : "flex-start"}
      bg={message.role === "user" ? "blue.500" : bgColor}
      color={message.role === "user" ? "white" : "inherit"}
      maxW="80%"
      p={2}
      rounded="lg"
    >
      {parts.length ? parts : "..."}
    </Box>
  );
};

const MIN_WIDTH = 320;
const MIN_HEIGHT = 480;
const INITIAL_WIDTH = 448;
const INITIAL_HEIGHT = 512;

export const AIChat = () => {
  const { isEnabled } = useFlag("ai-chat");
  const { appId, envId } = useEnvironment();
  const queryClient = useQueryClient();
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [chatState, setChatState] = useLocalStorage<ChatState>(
    `reflag-ai-chat.${appId}`,
    {
      id: generateId(),
      isOpen: false,
      messages: [],
    },
  );
  const [tempInput, setTempInput] = useState<string>("");
  const [historyIndex, setHistoryIndex] = useState<number>(-1);
  const { width, height, onMouseDown } = useResizable({
    minWidth: MIN_WIDTH,
    minHeight: MIN_HEIGHT,
    initialWidth: INITIAL_WIDTH,
    initialHeight: INITIAL_HEIGHT,
  });

  const { messages, input, setInput, handleInputChange, handleSubmit } =
    useChat({
      id: chatState.id,
      api: `${API_URL}/apps/${appId}/ai/chat`,
      credentials: "include",
      initialMessages: chatState.messages,
      onFinish: (message) => {
        setChatState((prevState) => {
          const messageIndex = prevState.messages.findIndex(
            (msg) => msg.id === message.id,
          );
          if (messageIndex >= 0) {
            // Update existing message while maintaining array order
            return {
              ...prevState,
              messages: prevState.messages.map((msg) =>
                msg.id === message.id ? message : msg,
              ),
            };
          }
          // Append new message
          return {
            ...prevState,
            messages: [...prevState.messages, message],
          };
        });

        // Invalidate queries when a tool is used
        for (const part of message.parts ?? []) {
          if (part.type === "tool-invocation") {
            switch (part.toolInvocation.toolName) {
              case "featureCreate":
                queryClient.invalidateQueries({
                  queryKey: featureQueryKeys.list(appId, envId),
                });
                break;
              case "featureAccess":
                // todo: resolve the flagId, args only contains the featureKey
                // queryClient.invalidateQueries({
                //   queryKey: flagQueryKeys.single(appId, flagId),
                // });
                // queryClient.invalidateQueries({
                //   queryKey: flagQueryKeys.versions(appId, flagId),
                // });
                break;
              case "updateFeatureStage":
                queryClient.invalidateQueries({
                  queryKey: featureQueryKeys.list(appId, envId),
                });
                // todo: resolve the flagId, args only contains the featureKey
                // queryClient.invalidateQueries({
                //   queryKey: flagQueryKeys.single(appId, flagId),
                // });
                // queryClient.invalidateQueries({
                //   queryKey: flagQueryKeys.versions(appId, flagId),
                // });
                break;
              default:
                break;
            }
          }
        }
      },
    });

  const onSubmit = useCallback(() => {
    setChatState((prevState) => ({
      ...prevState,
      messages: [
        ...prevState.messages,
        {
          id: `msg-${generateId()}`,
          createdAt: new Date(),
          role: "user",
          parts: [{ type: "text", text: input }],
          content: input,
        },
      ],
    }));
    handleSubmit();
  }, [input, handleSubmit, setChatState]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const textarea = e.target as HTMLTextAreaElement;

      if (input?.trim() && e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        onSubmit();
        return;
      }

      const history = [
        ...messages
          .filter((msg) => msg.role === "user")
          .map((msg) => msg.content),
      ];

      if (e.key === "ArrowUp" && isCursorAtEdge(textarea, "first")) {
        e.preventDefault();
        if (historyIndex === -1) {
          setTempInput(input);
        }
        const nextIndex = Math.min(historyIndex + 1, history.length - 1);
        setHistoryIndex(nextIndex);
        setInput(history[history.length - 1 - nextIndex]);
        return;
      }

      if (e.key === "ArrowDown" && isCursorAtEdge(textarea, "last")) {
        e.preventDefault();
        const nextIndex = historyIndex - 1;
        if (nextIndex >= 0) {
          setHistoryIndex(nextIndex);
          setInput(history[history.length - 1 - nextIndex]);
        } else {
          setHistoryIndex(-1);
          setInput(tempInput);
        }
        return;
      }
    },
    [
      input,
      messages,
      tempInput,
      setInput,
      setTempInput,
      onSubmit,
      historyIndex,
    ],
  );

  // Reset history index when chat changes
  useEffect(() => {
    setHistoryIndex(-1);
    setTempInput("");
  }, [chatState.id]);

  // Scroll to bottom whenever messages change or chat opens
  useEffect(() => {
    if (messages.length > 0 && chatContainerRef.current && chatState.isOpen) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages, chatState.isOpen]);

  const wrappedHandleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setHistoryIndex(-1);
      setTempInput(e.target.value);
      handleInputChange(e);
    },
    [handleInputChange],
  );

  if (!isEnabled) {
    return null;
  }

  return (
    <Popover
      key={appId}
      closeOnBlur={false}
      isOpen={chatState.isOpen}
      onClose={() => setChatState((prev) => ({ ...prev, isOpen: false }))}
      onOpen={() => setChatState((prev) => ({ ...prev, isOpen: true }))}
    >
      <PopoverTrigger>
        <IconButton
          aria-label="AI Chat"
          bottom={4}
          icon={<RiChatAiLine size={20} />}
          position="fixed"
          right={16}
          rounded="full"
          shadow="lg"
          size="md"
        />
      </PopoverTrigger>
      <PopoverContent
        height={height ? `${height}px` : undefined}
        minHeight={`${MIN_HEIGHT}px`}
        minWidth={`${MIN_WIDTH}px`}
        style={{ resize: "none", overflow: "hidden", position: "relative" }}
        width={width ? `${width}px` : "md"}
      >
        <Box
          aria-label="Resize chat window"
          color="veryDimmed"
          cursor="nwse-resize"
          left={-2}
          p={1}
          position="absolute"
          role="button"
          top={-2}
          transform="rotate(135deg)"
          onMouseDown={onMouseDown}
        >
          <RiFilter3Line size={20} />
        </Box>
        <PopoverArrow />
        <PopoverHeader
          alignItems="center"
          display="flex"
          justifyContent="space-between"
        >
          <HStack>
            <RiSparkling2Line size={16} />
            <Text fontSize="lg" fontWeight="medium">
              Chat with Reflag!
            </Text>
          </HStack>
          <ButtonGroup variant="ghost">
            <IconButton
              aria-label="Start a new chat"
              icon={<RiAddLine size={16} />}
              onClick={() => {
                setChatState((prev) => ({
                  ...prev,
                  id: generateId(),
                  messages: [],
                }));
              }}
            />
            <IconButton
              aria-label="Close chat window"
              icon={<RiCloseLine size={16} />}
              onClick={() => {
                setChatState((prev) => ({ ...prev, isOpen: false }));
              }}
            />
          </ButtonGroup>
        </PopoverHeader>
        <PopoverBody display="flex" flex="1 1 0" flexDirection="column" p={0}>
          <Flex
            ref={chatContainerRef}
            flex="1 1 0"
            flexDirection="column"
            overflowY="auto"
            px={6}
            py={4}
            width="full"
          >
            <Flex flexDirection="column" gap={3} marginTop="auto">
              {messages.map((message) => (
                <Message key={message.id} message={message} />
              ))}
            </Flex>
          </Flex>
          <HStack alignItems="flex-end" p={3} spacing={2} width="full">
            <Textarea
              minH={9}
              placeholder="Type your message..."
              resize="vertical"
              value={input}
              onChange={wrappedHandleInputChange}
              onKeyDown={handleKeyDown}
            />
            <IconButton
              aria-label="Send message"
              colorScheme="blue"
              icon={<RiSendPlaneFill size={16} />}
              size="md"
              onClick={onSubmit}
            />
          </HStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
